import { useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import { useFormContext } from "react-hook-form";
import SelectInstagramAccountWithConnect from "../../../../Meta/SelectInstagramAccountWithConnect";
import Select from "../../../../Form/Select";
import InstagramCollaboratorsField from "../../../../Platforms/Instagram/InstagramCollaboratorsField";
import { getExtensionFile } from "../../../../../utils/string";
import {
  IMAGE_FORMATS,
  INSTAGRAM_IMAGE_CONTENT_TYPE_OPTIONS,
  INSTAGRAM_VIDEO_CONTENT_TYPE_OPTIONS,
  VIDEO_FORMATS,
  INSTAGRAM_CONTENT_TYPE_CAROUSEL,
  INSTAGRAM_CONTENT_TYPE_STORIES,
} from "../../../../../utils/constants";
import {
  FIELD_REQUIRED,
  INSTAGRAM_ACCOUNT_PLACEHOLDER,
  TYPE_CONTENT_INSTAGRAM_LABEL,
  INSTAGRAM_COLLABORATORS_LABEL,
  INSTAGRAM_COLLABORATORS_PLACEHOLDER,
} from "../../../../../i18n/keysTranslations";

const InstagramPlatformFields = ({
  control,
  errors,
  currentItem,
  t,
  uniqueInstagramBusinessList,
  isCarouselMode = false,
  currentItems = [],
  onContentTypeChange,
}) => {
  const { setValue, watch } = useFormContext();
  const format = getExtensionFile(currentItem?.url);
  const isImage = IMAGE_FORMATS.includes(format);
  const isVideo = VIDEO_FORMATS.includes(format);
  const currentContentType = watch("platformsData.instagram.contentType");

  const isMultiImageCarousel = isCarouselMode && currentItems.length >= 2;
  const restrictToCarouselOnly = isMultiImageCarousel;

  // Collaborators are only available for feed posts and carousels, not stories
  const showCollaboratorsField =
    currentContentType !== INSTAGRAM_CONTENT_TYPE_STORIES;

  const contentTypeOptions = useMemo(() => {
    if (!currentItem?.url) return [];

    let options = [];
    if (isImage) {
      const mapped = INSTAGRAM_IMAGE_CONTENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      }));
      options = restrictToCarouselOnly
        ? mapped.filter(
            (option) => option.value === INSTAGRAM_CONTENT_TYPE_CAROUSEL
          )
        : mapped;
    }

    if (isVideo) {
      options = INSTAGRAM_VIDEO_CONTENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      }));
    }

    return options;
  }, [currentItem?.url, isImage, isVideo, restrictToCarouselOnly, t]);

  useEffect(() => {
    if (contentTypeOptions.length === 0) return;
    const isStillValid = contentTypeOptions.some(
      (opt) => opt.value === currentContentType
    );
    if (currentContentType && !isStillValid) {
      setValue(
        "platformsData.instagram.contentType",
        contentTypeOptions[0].value
      );
    }
  }, [contentTypeOptions, currentContentType, setValue]);

  useEffect(() => {
    if (isCarouselMode && currentItems.length >= 2 && !currentContentType) {
      const hasCarouselOption = contentTypeOptions.some(
        (opt) => opt.value === INSTAGRAM_CONTENT_TYPE_CAROUSEL
      );
      if (hasCarouselOption) {
        setValue(
          "platformsData.instagram.contentType",
          INSTAGRAM_CONTENT_TYPE_CAROUSEL
        );
      }
    }
  }, [
    isCarouselMode,
    currentItems.length,
    contentTypeOptions,
    setValue,
    currentContentType,
  ]);

  // Clear collaborators when switching to Stories
  useEffect(() => {
    if (currentContentType === INSTAGRAM_CONTENT_TYPE_STORIES) {
      setValue("platformsData.instagram.collaborators", []);
    }
  }, [currentContentType, setValue]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SelectInstagramAccountWithConnect
        variant="filled"
        name="platformsData.instagram.account"
        label={t(INSTAGRAM_ACCOUNT_PLACEHOLDER)}
        fullWidth
        control={control}
        errors={errors}
        uniqueInstagramBusinessList={uniqueInstagramBusinessList}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }}
      />

      <Select
        variant="filled"
        name="platformsData.instagram.contentType"
        label={t(TYPE_CONTENT_INSTAGRAM_LABEL)}
        options={contentTypeOptions}
        fullWidth
        control={control}
        errors={errors}
        disabled={!currentItem?.url}
        validationChange={(value) => {
          if (onContentTypeChange) {
            return onContentTypeChange("instagram", value);
          }
          return true;
        }}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
        }}
      />

      {showCollaboratorsField && (
        <InstagramCollaboratorsField
          variant="filled"
          name="platformsData.instagram.collaborators"
          label={t(INSTAGRAM_COLLABORATORS_LABEL)}
          placeholder={t(INSTAGRAM_COLLABORATORS_PLACEHOLDER)}
          fullWidth
          control={control}
          errors={errors}
        />
      )}
    </Box>
  );
};

export default InstagramPlatformFields;
